import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { segmentedTabsTriggerClassName, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { type ForwardedRef, forwardRef, type ReactNode } from 'react';
export interface OptionTableFilter<T> {
  value: T;
  label: string;
  isActive?: boolean;
  action?: (option: OptionTableFilter<T>) => void;
  isDisabled?: boolean;
  tooltip?: ReactNode;
}
export interface TableFilterProps<T> {
  id: string;
  options: OptionTableFilter<T>[];
  disabled?: boolean;
}

// Segmented styling from @repo/ui on plain buttons: these options are independent filters, not
// tabs, so Radix Tabs' roles and roving tabindex don't fit. `data-state` is the styling hook
// those classes key off; `aria-pressed` is what assistive tech reads.
const optionClassName = cn(segmentedTabsTriggerClassName, 'disabled:pointer-events-none disabled:opacity-50', 'focus-visible:border-ring focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]');
function TableFilterInner(t0, ref) {
  const $ = _c(16);
  let id;
  let options;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      options,
      id,
      disabled: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = id;
    $[2] = options;
    $[3] = props;
    $[4] = t1;
  } else {
    id = $[1];
    options = $[2];
    props = $[3];
    t1 = $[4];
  }
  const disabled = t1 === undefined ? false : t1;
  if (options.length === 0) {
    return null;
  }
  let t2;
  if ($[5] !== disabled || $[6] !== id || $[7] !== options) {
    let t3;
    if ($[9] !== disabled || $[10] !== id) {
      t3 = option => <Tooltip key={id + option.value} delayDuration={500}><TooltipTrigger asChild={true}><button type="button" aria-pressed={option.isActive ?? false} data-state={option.isActive ? "active" : "inactive"} disabled={disabled || option.isDisabled} onClick={() => option.action?.(option)} className={optionClassName}>{option.label}</button></TooltipTrigger>{option.tooltip && <TooltipContent>{option.tooltip}</TooltipContent>}</Tooltip>;
      $[9] = disabled;
      $[10] = id;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = options.map(t3);
    $[5] = disabled;
    $[6] = id;
    $[7] = options;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[12] !== props || $[13] !== ref || $[14] !== t2) {
    t3 = <div ref={ref} {...props} className="gap-xs flex flex-wrap items-center">{t2}</div>;
    $[12] = props;
    $[13] = ref;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

// Create a generic version of forwardRef
// oxlint-disable-next-line typescript/consistent-type-assertions
export const TableFilter = forwardRef(TableFilterInner) as <T>(props: TableFilterProps<T> & {
  ref?: ForwardedRef<HTMLDivElement>;
}) => ReturnType<typeof TableFilterInner>;